import React from 'react';
import PropTypes from 'prop-types';
import { Field } from 'redux-form';
import { t } from 'i18next';
import { Accessorials, Measurements, ShipmentSummary } from './ContentBox';
import { baseServiceLevelCode } from '../../../utils/enums';
import Row from '../../ReactComponent/Row';
import Column from '../../ReactComponent/Column';
import Edit from '../../../assets/icons/Edit.svg';
import {
  CategoryTitle,
  Icon,
  LineButton,
  FieldGroupLabel,
  DisclaimerNote,
  DisclaimerText,
  DisclaimerGroup,
  StepPageTitle,
  StepPageSubtitle,
} from '../css';
import {
  formatMoney,
  formatDisplayDateHuman,
  isCanadianAddress,
} from '../../../utils/func';
import RateCard from '../../ReactComponent/RateCard/RateCard';
import NextPreviousButtons from '../../../lib/ui/form_elements/NextPreviousButtons';
// import {
//   Card, Head, TitleRow, Body, InfoRow,
// } from '../../ReactComponent/RateCard/css';
// import { PBold, PThin } from '../../../theme/typography';

export const SummaryForm = ({
  formData = {},
  step,
  editStepOne,
  editStepTwo,
  editStepThree,
  parentFields,
}) => {
  const scDatasource = parentFields.data.datasource;
  const title1 = (step === 4) ? 'step5CategoryTitle3' : 'step4CategoryTitle2';
  const title2 = (step === 4) ? 'step5CategoryTitle4' : 'step4CategoryTitle3';
  const title3 = (step === 4) ? 'step5CategoryTitle5' : 'step4CategoryTitle4';

  return (
    <>

      <CategoryTitle>
        {scDatasource[title1] && scDatasource[title1].value}
        {editStepOne && (
          <LineButton onClick={editStepOne}>
            {t('Edit')}
            <Icon className="icon right" src={Edit} alt="edit step one" />
          </LineButton>
        )}
      </CategoryTitle>
      <ShipmentSummary formData={formData} />

      <CategoryTitle>
        {scDatasource[title2] && scDatasource[title2].value}
        {editStepTwo && (
          <LineButton onClick={editStepTwo}>
            {t('Edit')}
            <Icon className="icon right" src={Edit} alt="edit step two" />
          </LineButton>
        )}
      </CategoryTitle>
      <Measurements formData={formData} />

      <CategoryTitle>
        {scDatasource[title3] && scDatasource[title3].value}
        {editStepThree && (
          <LineButton onClick={editStepThree}>
            {t('Edit')}
            <Icon className="icon right" src={Edit} alt="edit step three" />
          </LineButton>
        )}
      </CategoryTitle>
      <Accessorials formData={formData} accList={parentFields.data.accessorialsItem.children} />

    </>
  );
};

const Step4 = ({
  formData = {},
  accessorialsBase,
  serviceLevelCode,
  step,
  editStepOne,
  editStepTwo,
  editStepThree,
  serviceLevelQuotes,
  // quoteApproved,
  putReduxServiceLevelCode,
  parentFields,
  billingAccounts,
  lastStep,
  valid,
  serviceLevels,
}) => {
  const {
    shipperResidentialAddress, consigneeResidentialAddress, shipperAddress, consigneeAddress,
  } = formData;
  let protectedAccount = billingAccounts && billingAccounts.some((a) => a.accountNumber === formData.billTo && a.protected);
  if (formData && formData.division === 'Sameday' && formData.paymentMethod) {
    if (formData.paymentMethod === 'Collect' || ((formData.paymentMethod === 'Third Party' && protectedAccount)
      || formData.billToMethod === 'EnterBillTo')) {
      protectedAccount = true;
    }
  }

  const allServicesWouldBeHidden = serviceLevelQuotes.length > 0
    && serviceLevelQuotes.every((item) => {
      const totalAmountValue = (item.totalAmount && item.totalAmount.value) ? item.totalAmount.value : 0;
      return totalAmountValue === 0
        || item.isApproved === false
        || ((item.serviceLevelCode === 'R2R' || item.serviceLevelCode === 'R2D')
          && shipperResidentialAddress && !consigneeResidentialAddress);
    });

  // When all services would be hidden, pick base services to show
  // Always include R2T alongside the primary fallback so vendors can select 2-person delivery
  const baseServicePriority = [
    baseServiceLevelCode.residencial1,
    baseServiceLevelCode.domesticLTL,
    baseServiceLevelCode.transborderLTL,
    baseServiceLevelCode.residencial2,
    baseServiceLevelCode.residencial3,
  ];
  // const formServiceCode = (serviceLevels && serviceLevels.code) || serviceLevelCode;
  const primaryFallbackCode = allServicesWouldBeHidden
    ? baseServicePriority.find((code) => serviceLevelQuotes.some((s) => s.serviceLevelCode === code))
    : null;
  const fallbackServiceCodes = allServicesWouldBeHidden
    ? [primaryFallbackCode, baseServiceLevelCode.residencial2].filter(
      (code) => code && serviceLevelQuotes.some((s) => s.serviceLevelCode === code),
    )
    : [];
  // const fallbackServiceCode = primaryFallbackCode;

  // Get service level label from multiple sources to handle timing
  // const fallbackService = serviceLevelQuotes.find(
  //   (s) => s.serviceLevelCode === (fallbackServiceCode || serviceLevelCode),
  // );
  // const serviceLevelLabel = (fallbackService && fallbackService.description)
  //   || (serviceLevels && serviceLevels.description)
  //   || (serviceLevelQuotes[0] && serviceLevelQuotes[0].description)
  //   || '';

  const serviceLevelOptions = serviceLevelQuotes.map((item) => {
    const chargesObj = item.charges.map((charge) => {
      const amountValue = (charge.amount && charge.amount.value) ? charge.amount.value : 0;

      const filteredArr = accessorialsBase.filter((item) => item.code === charge.chargeCode);
      const chargeDesc = (filteredArr.length > 0) ? filteredArr[0].description : charge.description;

      return { amount: formatMoney(amountValue, protectedAccount), description: chargeDesc, amountValue };
    });

    const totalChargesValue = (item.totalCharges && item.totalCharges.value) ? item.totalCharges.value : 0;
    const taxValue = (item.tax && item.tax.value) ? item.tax.value : 0;
    const totalAmountValue = (item.totalAmount && item.totalAmount.value) ? item.totalAmount.value : 0;
    const totalAmountCurrencyCode = (item.totalAmount && item.totalAmount.currencyCode) ? item.totalAmount.currencyCode : '';

    const showAsUnapproved = fallbackServiceCodes.includes(item.serviceLevelCode);

    const privateResidenceDelivery = formData?.accessorials?.accPRESDL ?? '';
    const privateResidencePickup = formData?.accessorials?.accPRESPU ?? '';

    const isResidentialCondition = (shipperAddress && isCanadianAddress(shipperAddress)) && (consigneeAddress && isCanadianAddress(consigneeAddress))
      && (
        shipperResidentialAddress
        || consigneeResidentialAddress
        || privateResidenceDelivery?.selected
        || privateResidencePickup?.selected
      );

    const residentialServices = ['R1T', 'R2T', 'R1NS'];
    const isResidentialService = residentialServices.includes(item.serviceLevelCode);
    const isResidentialServiceVisible = isResidentialCondition && isResidentialService;

    return (
      {
        label: item.description,
        value: item.serviceLevelCode,
        charges: chargesObj,
        // expDate: formatDisplayDateHuman(item.expectedDeliveryDate),
        expDate: item.expectedDeliveryDate ? formatDisplayDateHuman(new Date(item.expectedDeliveryDate)) : '',
        totalCharges: formatMoney(totalChargesValue, protectedAccount),
        totalChargesValue,
        tax: formatMoney(taxValue, protectedAccount),
        taxValue,
        totalAmount: formatMoney(totalAmountValue, protectedAccount),
        defaultOption: serviceLevelCode === item.serviceLevelCode,
        toBeHidden: (() => {
          if (showAsUnapproved) return false;

          if (isResidentialServiceVisible) return false;

          if (isResidentialService && !isResidentialCondition) return true;

          if (totalAmountValue === 0) return true;

          if (item.isApproved === false) return true;

          if (
            (item.serviceLevelCode === 'R2R' || item.serviceLevelCode === 'R2D')
            && shipperResidentialAddress
            && !consigneeResidentialAddress
          ) return true;

          return false;
        })(),
        noServicesAvailable: showAsUnapproved,
        totalAmountCurrencyCode,
        totalAmountValue,
        isApproved: item.isApproved,
      }
    );
  });

  // If fallback service is not in API response, add a synthetic option
  // if (
  //   fallbackServiceCode
  //   && !serviceLevelQuotes.some((s) => s.serviceLevelCode === fallbackServiceCode)
  // ) {
  //   serviceLevelOptions.push({
  //     label: (serviceLevels && serviceLevels.description) || fallbackServiceCode,
  //     value: fallbackServiceCode,
  //     charges: [],
  //     expDate: '',
  //     totalCharges: formatMoney(0, protectedAccount),
  //     totalChargesValue: 0,
  //     tax: formatMoney(0, protectedAccount),
  //     taxValue: 0,
  //     totalAmount: formatMoney(0, protectedAccount),
  //     defaultOption: true,
  //     toBeHidden: false,
  //     noServicesAvailable: true,
  //     totalAmountCurrencyCode: '',
  //     totalAmountValue: 0,
  //   });
  // }

  if (step === 3) {
    return (
      <>
        <Row marginTop={16} marginBottom={40}>
          <Column $tablet={6} $mobile={12}>
            <StepPageTitle>{t('Service')}</StepPageTitle>
            <StepPageSubtitle>{t('Step5SubTitle')}</StepPageSubtitle>
          </Column>
          <Column $tablet={6} $mobile={12}>
            {valid && (
              <NextPreviousButtons
                currentStep={step}
                totalSteps={lastStep}
                isValid={valid}
                nextObj={{
                  label: t('RequestQuote'),
                  type: 'submit',
                  action: null,
                  disabled: (!valid),
                }}
                onlyNext
                noMargins
              />
            )}
          </Column>
        </Row>

        <Row>
          <Column $mobile={12}>
            {/* {
              !quoteApproved && (
                <Card>
                  <Head>
                    <TitleRow>
                      <PBold className="large">{serviceLevelLabel}</PBold>
                    </TitleRow>
                  </Head>
                  <Body>
                    <InfoRow>
                      <PThin>{t('CreateQuoteNotApproved')}</PThin>
                    </InfoRow>
                  </Body>
                </Card>
              )
            } */}
            {/* {
              quoteApproved
              && ( */}
            {/* <> */}
            <FieldGroupLabel>{t('Options')}</FieldGroupLabel>
            <DisclaimerGroup>
              <DisclaimerNote>{t('Note')}</DisclaimerNote>
              <DisclaimerText>
                :
                {t('Disclaimer')}
              </DisclaimerText>
            </DisclaimerGroup>
            <Field
              name="rateReview"
              component={RateCard}
              options={serviceLevelOptions}
              current={serviceLevelCode}
              putReduxServiceLevelCode={putReduxServiceLevelCode}
              serviceLevels={serviceLevels}
              page="create-quote"
            />
            {/* </>
              )
            } */}

          </Column>
        </Row>

        <SummaryForm
          step={3}
          formData={formData}
          editStepOne={editStepOne}
          editStepTwo={editStepTwo}
          editStepThree={editStepThree}
          parentFields={parentFields}
        />
      </>
    );
  }

  return null;
};

SummaryForm.propTypes = {
  step: PropTypes.number,
  formData: PropTypes.object,
  editStepOne: PropTypes.func,
  editStepTwo: PropTypes.func,
  editStepThree: PropTypes.func,
  parentFields: PropTypes.object,
};
Step4.propTypes = {
  formData: PropTypes.object,
  accessorialsBase: PropTypes.array,
  serviceLevelCode: PropTypes.string,
  step: PropTypes.number,
  lastStep: PropTypes.number,
  valid: PropTypes.bool,
  editStepOne: PropTypes.func,
  editStepTwo: PropTypes.func,
  editStepThree: PropTypes.func,
  serviceLevelQuotes: PropTypes.array,
  // quoteApproved: PropTypes.bool,
  putReduxServiceLevelCode: PropTypes.func,
  parentFields: PropTypes.object,
  billingAccounts: PropTypes.array,
  serviceLevels: PropTypes.string,
};

export default Step4;
